import {
  RefreshControl,
  ScrollView,
  Text,
  View,
  Pressable,
} from 'react-native';
import { useCallback, useMemo, useState } from 'react';
import { useIsFocused, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { trpc } from '../../lib/trpc';
import { groupAndSortCategories } from '../../lib/category-config';
import { useColors } from '../../lib/colors';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import { formatCurrency, formatPercent } from '../../lib/utils';
import {
  Button,
  Card,
  CurrencyInput,
  ErrorState,
  FormErrorBanner,
  LoadingSpinner,
  Money,
  PageContainer,
  PageHeader,
  ProgressBar,
  SectionHeader,
  WebHead,
} from '../../components';

type CheckInDecision = 'keep_current' | 'revise_up' | 'revise_down' | 'review_outliers';

type CheckInCategory = {
  budgetCategoryId: number;
  categoryName: string;
  plaidPrimary: string;
  currentMonthlyBudget: number;
  pendingMonthlyBudget: number | null;
  actualMonthlyAverage: number;
  suggestedMonthlyBudget: number;
  actualTotal: number;
  transactionCount: number;
  outlierTotal: number;
  lifeEventExcludedTotal: number;
  lifeEventExcludedCount: number;
  decision: CheckInDecision;
  reasoning: string;
  isDiscretionary: boolean;
  isEditable: boolean;
  outliers: Array<{
    id: string;
    date: string;
    name: string;
    merchantName: string | null;
    amount: number;
  }>;
};

type CheckInResult = {
  method: 'deterministic_v1' | 'legacy_ai';
  periodStart: string;
  periodEnd: string;
  dayCount: number;
  usedAi: boolean;
  summary: string;
  baselineMonthlyBudgetTotal: number;
  currentMonthlyBudgetTotal: number;
  actualMonthlyAverageTotal: number;
  suggestedMonthlyBudgetTotal: number;
  lifeEventExcludedTotal: number;
  lifeEventExcludedCount: number;
  taxPaymentActualTotal: number;
  taxPaymentTransactionCount: number;
  categories: CheckInCategory[];
};

type DisplayCategory = CheckInCategory & {
  displayId: string;
  actual: number;
  budgeted: number;
};

function formatDate(value: string): string {
  return new Date(`${value}T00:00:00Z`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function formatSignedCurrency(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  if (rounded === 0) return formatCurrency(0);
  return `${rounded > 0 ? '+' : '-'}${formatCurrency(Math.abs(rounded))}`;
}

function formatMonthlyCurrency(value: number): string {
  return `${formatCurrency(value)}/mo`;
}

function formatSignedMonthlyCurrency(value: number): string {
  return `${formatSignedCurrency(value)}/mo`;
}

function budgetDeltaTextClass(value: number): string {
  if (value < 0) return 'text-green-700 dark:text-green-400';
  if (value > 0) return 'text-amber-700 dark:text-amber-400';
  return 'text-gray-700 dark:text-gray-300';
}

function decisionMeta(decision: CheckInCategory['decision']): {
  label: string;
  pillClass: string;
  textClass: string;
} {
  switch (decision) {
    case 'revise_up':
      return {
        label: 'Raise if recurring',
        pillClass: 'bg-red-50 dark:bg-red-900/30',
        textClass: 'text-red-600 dark:text-red-400',
      };
    case 'revise_down':
      return {
        label: 'Lower if sustainable',
        pillClass: 'bg-green-50 dark:bg-green-900/30',
        textClass: 'text-green-700 dark:text-green-400',
      };
    case 'review_outliers':
      return {
        label: 'Review outliers',
        pillClass: 'bg-amber-50 dark:bg-amber-900/30',
        textClass: 'text-amber-700 dark:text-amber-400',
      };
    default:
      return {
        label: 'Keep current',
        pillClass: 'bg-gray-50 dark:bg-gray-800',
        textClass: 'text-gray-600 dark:text-gray-400',
      };
  }
}

function SummaryMetric({
  label,
  value,
  tone = 'neutral',
  compact = false,
}: {
  label: string;
  value: number;
  tone?: 'neutral' | 'positive' | 'warning';
  compact?: boolean;
}) {
  const valueClass =
    tone === 'positive'
      ? 'text-green-700 dark:text-green-400'
      : tone === 'warning'
        ? 'text-amber-700 dark:text-amber-400'
        : 'text-gray-900 dark:text-gray-100';
  const containerPadding = compact ? 'px-2 py-2' : 'px-3 py-3';
  const labelSize = compact ? 'text-[10px]' : 'text-[11px]';
  const valueSize = compact ? 'text-sm' : 'text-lg';

  return (
    <View className={`${containerPadding} flex-1 rounded-lg border border-gray-100 dark:border-gray-700`}>
      <Text className={`${labelSize} font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400`}>
        {label}
      </Text>
      <Money weight={500} className={`mt-1 ${valueSize} ${valueClass}`}>
        {formatMonthlyCurrency(value)}
      </Money>
    </View>
  );
}

function RunningBudgetMetric({
  label,
  value,
  tone = 'neutral',
  compact = false,
}: {
  label: string;
  value: string;
  tone?: 'neutral' | 'positive' | 'warning';
  compact?: boolean;
}) {
  const valueClass =
    tone === 'positive'
      ? 'text-green-700 dark:text-green-400'
      : tone === 'warning'
        ? 'text-amber-700 dark:text-amber-400'
        : 'text-gray-900 dark:text-gray-100';
  const containerPadding = compact ? 'px-2 py-2' : 'min-w-[112px] px-3 py-2';
  const labelSize = compact ? 'text-[10px]' : 'text-[10.5px]';
  const valueSize = compact ? 'text-xs' : 'text-sm';

  return (
    <View className={`${containerPadding} flex-1 rounded-lg bg-gray-50 dark:bg-gray-900/40`}>
      <Text className={`${labelSize} font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400`}>
        {label}
      </Text>
      <Money weight={500} className={`mt-0.5 ${valueSize} ${valueClass}`}>
        {value}
      </Money>
    </View>
  );
}

function ReviewTotalsPanel({
  checkIn,
  isDesktop,
  isDemo,
  totalDifference,
  markReviewedPending,
  onDoneReviewing,
}: {
  checkIn: CheckInResult;
  isDesktop: boolean;
  isDemo: boolean;
  totalDifference: number;
  markReviewedPending: boolean;
  onDoneReviewing: () => void;
}) {
  const pendingMonthlyBudgetTotal = Math.round(
    checkIn.categories.reduce(
      (sum, category) => sum + (category.pendingMonthlyBudget ?? category.currentMonthlyBudget),
      0,
    ) * 100,
  ) / 100;
  const runningBudgetDelta = Math.round(
    (pendingMonthlyBudgetTotal - checkIn.currentMonthlyBudgetTotal) * 100,
  ) / 100;
  const runningDeltaTone =
    runningBudgetDelta < 0 ? 'positive' : runningBudgetDelta > 0 ? 'warning' : 'neutral';
  const pendingChangeCount = checkIn.categories.filter(
    (category) => category.pendingMonthlyBudget != null,
  ).length;
  const compact = !isDesktop;

  return (
    <Card className="rounded-xl">
      <View className="flex-row gap-2">
        <SummaryMetric
          label="Current budget"
          value={checkIn.currentMonthlyBudgetTotal}
          compact={compact}
        />
        <SummaryMetric
          label="90-day actual"
          value={checkIn.actualMonthlyAverageTotal}
          compact={compact}
          tone={
            checkIn.actualMonthlyAverageTotal > checkIn.currentMonthlyBudgetTotal
              ? 'warning'
              : 'positive'
          }
        />
        <SummaryMetric
          label="Suggested"
          value={checkIn.suggestedMonthlyBudgetTotal}
          compact={compact}
          tone={totalDifference <= 0 ? 'positive' : 'warning'}
        />
      </View>

      <View className="mt-3 flex-row gap-2">
        <RunningBudgetMetric
          label="Before changes"
          value={formatMonthlyCurrency(checkIn.currentMonthlyBudgetTotal)}
          compact={compact}
        />
        <RunningBudgetMetric
          label="Pending change"
          value={formatSignedMonthlyCurrency(runningBudgetDelta)}
          tone={runningDeltaTone}
          compact={compact}
        />
        <RunningBudgetMetric
          label="After changes"
          value={formatMonthlyCurrency(pendingMonthlyBudgetTotal)}
          tone={runningDeltaTone}
          compact={compact}
        />
      </View>
      <View className="mt-4 flex-row justify-end">
        <Button
          size="sm"
          disabled={isDemo || markReviewedPending}
          loading={markReviewedPending}
          onPress={onDoneReviewing}
        >
          {pendingChangeCount > 0 ? 'Apply changes and complete review' : 'Complete review'}
        </Button>
      </View>
    </Card>
  );
}

function ReviewNotesPanel({
  checkIn,
  totalDifference,
}: {
  checkIn: CheckInResult;
  totalDifference: number;
}) {
  if (
    totalDifference >= 0 &&
    checkIn.lifeEventExcludedTotal <= 0 &&
    checkIn.taxPaymentActualTotal <= 0
  ) {
    return null;
  }
  const lifeEventClass = totalDifference < 0
    ? 'mt-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 px-3 py-2'
    : 'rounded-lg bg-amber-50 dark:bg-amber-900/20 px-3 py-2';

  return (
    <Card>
      {totalDifference < 0 && (
        <Text className="text-xs text-green-700 dark:text-green-400">
          Accepting the lower sustainable categories would reduce planned spending by{' '}
          {formatCurrency(Math.abs(totalDifference) * 12)} per year. Keeping that up could improve your financial independence timeline.
        </Text>
      )}
      {checkIn.lifeEventExcludedTotal > 0 && (
        <View className={lifeEventClass}>
          <Text className="text-xs text-amber-700 dark:text-amber-400">
            {formatCurrency(checkIn.lifeEventExcludedTotal)} across{' '}
            {checkIn.lifeEventExcludedCount} life-event transaction
            {checkIn.lifeEventExcludedCount === 1 ? '' : 's'} stayed out of this review.
          </Text>
        </View>
      )}
      {checkIn.taxPaymentActualTotal > 0 && (
        <Text className="mt-2 text-[11px] leading-4 text-sky-600 dark:text-sky-300">
          * {formatCurrency(checkIn.taxPaymentActualTotal, { decimals: 0 })} across{' '}
          {checkIn.taxPaymentTransactionCount} tax payment
          {checkIn.taxPaymentTransactionCount === 1 ? ' was' : 's were'} excluded from budget actuals because taxes are modeled separately.
        </Text>
      )}
    </Card>
  );
}

export default function BudgetCheckInScreen() {
  const router = useRouter();
  const isFocused = useIsFocused();
  const isDesktop = useIsDesktop();
  const c = useColors();
  const utils = trpc.useUtils();
  const [refreshing, setRefreshing] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
  const [budgetDraft, setBudgetDraft] = useState('');
  const [savingCategoryId, setSavingCategoryId] = useState<string | null>(null);
  const [budgetError, setBudgetError] = useState<string | null>(null);

  const householdQ = trpc.households.get.useQuery(undefined, { enabled: isFocused });
  const checkInQ = trpc.budget.checkIn.useQuery(undefined, {
    enabled: isFocused,
    refetchOnMount: 'always',
  });
  const stageCategoryMutation = trpc.budget.stageCheckInCategory.useMutation();
  const markReviewedMutation = trpc.budget.markCheckInReviewed.useMutation({
    onSuccess: () => {
      void utils.budget.checkInStatus.invalidate();
    },
  });

  const checkIn = (checkInQ.data ?? null) as CheckInResult | null;
  const isDemo = householdQ.data?.isDemo ?? false;

  const categories: DisplayCategory[] = useMemo(
    () =>
      (checkIn?.categories ?? []).map((category) => ({
        ...category,
        displayId: `check-in-${category.budgetCategoryId}`,
        actual: category.actualMonthlyAverage,
        budgeted: category.currentMonthlyBudget,
      })),
    [checkIn?.categories],
  );

  const groups = useMemo(() => groupAndSortCategories(categories), [categories]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await checkInQ.refetch();
    setRefreshing(false);
  }, [checkInQ]);

  const stageCategoryBudget = async (category: CheckInCategory, amount: number) => {
    setBudgetError(null);
    if (isDemo) {
      setBudgetError('Demo budgets are read-only.');
      return;
    }
    if (!category.isEditable) {
      setBudgetError('This budget category is derived from a liability and must be updated there.');
      return;
    }
    if (!Number.isFinite(amount) || amount < 0) {
      setBudgetError('Please enter a valid monthly budget amount.');
      return;
    }

    try {
      setSavingCategoryId(`check-in-${category.budgetCategoryId}`);
      await stageCategoryMutation.mutateAsync({
        budgetCategoryId: category.budgetCategoryId,
        amount,
      });
      await Promise.allSettled([
        checkInQ.refetch(),
        utils.budget.checkInStatus.invalidate(),
      ]);
      setEditingCategoryId(null);
      setBudgetDraft('');
    } catch {
      setBudgetError('Could not stage this budget change. Please try again.');
    } finally {
      setSavingCategoryId(null);
    }
  };

  const startEditing = (category: CheckInCategory) => {
    setEditingCategoryId(`check-in-${category.budgetCategoryId}`);
    setBudgetDraft(String(
      Math.round((category.pendingMonthlyBudget ?? category.suggestedMonthlyBudget) * 100) / 100,
    ));
    setBudgetError(null);
  };

  const handleCompleteReview = async () => {
    setBudgetError(null);
    try {
      await markReviewedMutation.mutateAsync();
      await Promise.allSettled([
        utils.budget.checkInStatus.invalidate(),
        utils.budget.getEntries.invalidate(),
        utils.budget.perDiem.invalidate(),
        utils.budget.monthlyDiscretionarySummary.invalidate(),
        utils.fire.getRecalcStatus.invalidate(),
      ]);
      router.replace('/(app)/budget');
    } catch {
      setBudgetError('Could not mark this check-in reviewed. Please try again.');
    }
  };

  const pendingMonthlyBudgetTotal = checkIn
    ? checkIn.categories.reduce(
      (sum, category) => sum + (category.pendingMonthlyBudget ?? category.currentMonthlyBudget),
      0,
    )
    : 0;
  const totalDifference = checkIn
    ? checkIn.suggestedMonthlyBudgetTotal - pendingMonthlyBudgetTotal
    : 0;

  if (checkInQ.isLoading) {
    return (
      <LoadingSpinner message="Preparing your 90-day budget check-in." />
    );
  }

  const screenError = checkInQ.error;
  if (screenError) {
    return (
      <ErrorState
        error={screenError}
        screen="budget-check-in"
        metadata={{ failedQueries: 'budget.checkIn' }}
        onRetry={onRefresh}
      />
    );
  }

  return (
    <>
      <WebHead>
        <title>90-Day Budget Check-In - Per Diem</title>
      </WebHead>
      <ScrollView
        className="flex-1 bg-canvas dark:bg-canvas-dark"
        stickyHeaderIndices={checkIn ? [1] : undefined}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor="#2B7A50"
          />
        }
      >
        <PageContainer className={isDesktop ? 'pb-2' : 'p-4 pb-0'}>
          <PageHeader
            title="90-Day Budget Check-In"
            subtitle={
              checkIn
                ? `${formatDate(checkIn.periodStart)} - ${formatDate(checkIn.periodEnd)}`
                : 'Recent spending review'
            }
            action={
              <Pressable
                onPress={() => router.push('/(app)/budget')}
                className="flex-row items-center gap-1"
                hitSlop={8}
              >
                <Ionicons name="pie-chart-outline" size={16} color={c.textSecondary} />
                <Text className="text-xs text-gray-600 dark:text-gray-400">
                  Budget
                </Text>
              </Pressable>
            }
          />
        </PageContainer>

        {checkIn ? (
          <View className="z-10 bg-canvas dark:bg-canvas-dark border-b border-gray-100/80 dark:border-gray-800/80">
            <PageContainer className={isDesktop ? 'py-3' : 'px-4 py-3'}>
              <ReviewTotalsPanel
                checkIn={checkIn}
                isDesktop={isDesktop}
                isDemo={isDemo}
                totalDifference={totalDifference}
                markReviewedPending={markReviewedMutation.isPending}
                onDoneReviewing={() => void handleCompleteReview()}
              />
            </PageContainer>
          </View>
        ) : (
          <View />
        )}

        <PageContainer className={isDesktop ? 'pt-2' : 'p-4'}>
          {!checkIn ? (
            <Card className="items-center py-8">
              <Ionicons name="receipt-outline" size={28} color={c.textTertiary} />
              <Text className="mt-3 text-sm text-gray-500 dark:text-gray-400 text-center">
                Not enough recent spending data for a budget check-in yet.
              </Text>
            </Card>
          ) : (
            <View className="gap-4">
              <FormErrorBanner error={budgetError} />
              <ReviewNotesPanel checkIn={checkIn} totalDifference={totalDifference} />

              {groups.map((group) => {
                const groupActual = group.categories.reduce(
                  (sum, category) => sum + category.actualMonthlyAverage,
                  0,
                );
                const groupBudget = group.categories.reduce(
                  (sum, category) => sum + category.currentMonthlyBudget,
                  0,
                );
                const groupSuggested = group.categories.reduce(
                  (sum, category) => sum + category.suggestedMonthlyBudget,
                  0,
                );
                const groupPendingBudget = group.categories.reduce(
                  (sum, category) =>
                    sum + (category.pendingMonthlyBudget ?? category.currentMonthlyBudget),
                  0,
                );
                const groupSuggestedDelta = groupSuggested - groupBudget;
                const groupPendingDelta = groupPendingBudget - groupBudget;

                return (
                  <Card key={group.plaidPrimary}>
                      <SectionHeader
                        title={group.label}
                        action={
                          <View className="items-end">
                            <Money
                              weight={500}
                              className={`text-xs ${budgetDeltaTextClass(groupPendingDelta)}`}
                            >
                              {formatSignedMonthlyCurrency(groupPendingDelta)}
                            </Money>
                            <Text className="text-[10.5px] text-gray-400 dark:text-gray-500">
                              pending change
                            </Text>
                          </View>
                        }
                      />

                      <View className="mt-2 flex-row gap-2">
                        <View className="flex-1">
                          <Text className="text-[11px] text-gray-400 dark:text-gray-500">
                            Current
                          </Text>
                          <Money className="text-sm text-gray-800 dark:text-gray-200">
                            {formatMonthlyCurrency(groupBudget)}
                          </Money>
                        </View>
                        <View className="flex-1">
                          <Text className="text-[11px] text-gray-400 dark:text-gray-500">
                            90-day actual
                          </Text>
                          <Money className="text-sm text-gray-800 dark:text-gray-200">
                            {formatMonthlyCurrency(groupActual)}
                          </Money>
                        </View>
                        <View className="flex-1">
                          <Text className="text-[11px] text-gray-400 dark:text-gray-500">
                            Suggested
                          </Text>
                          <Money className={`text-sm ${budgetDeltaTextClass(groupSuggestedDelta)}`}>
                            {formatMonthlyCurrency(groupSuggested)}
                          </Money>
                        </View>
                      </View>

                      {groupBudget > 0 && (
                        <View className="mt-3 flex-row items-center gap-2">
                          <View className="flex-1">
                            <ProgressBar
                              value={groupActual / groupBudget}
                              color={groupActual > groupBudget ? 'bg-amber-500' : 'bg-green-500'}
                              height={4}
                            />
                          </View>
                          <Text className="text-xs font-medium text-gray-500 dark:text-gray-400 min-w-[45px] text-right">
                            {formatPercent(groupActual / groupBudget)}
                          </Text>
                        </View>
                      )}

                      <View className="mt-3 border-t border-gray-50 dark:border-gray-700">
                        {group.categories.map((category, index) => {
                        const displayId = `check-in-${category.budgetCategoryId}`;
                        const isEditing = editingCategoryId === displayId;
                        const proposedMonthlyBudget =
                          category.pendingMonthlyBudget ?? category.currentMonthlyBudget;
                        const pendingMonthlyDelta =
                          proposedMonthlyBudget - category.currentMonthlyBudget;
                        const hasPendingChange = category.pendingMonthlyBudget != null;
                        const meta = decisionMeta(category.decision);
                        const budgetRatio = category.currentMonthlyBudget > 0
                          ? category.actualMonthlyAverage / category.currentMonthlyBudget
                          : 0;
                        const isLast = index === group.categories.length - 1;
                        const monthlySavings = Math.max(
                          0,
                          category.currentMonthlyBudget - category.suggestedMonthlyBudget,
                        );
                        const shouldShowReasoning =
                          category.reasoning.trim().length > 0 &&
                          !(category.decision === 'revise_down' && monthlySavings > 0);

                        return (
                          <View
                            key={displayId}
                            className={`py-3 ${isLast ? '' : 'border-b border-gray-50 dark:border-gray-700'}`}
                          >
                            <View className="flex-row items-start justify-between gap-3">
                              <View className="flex-1">
                                <Text
                                  className="text-sm font-semibold text-gray-900 dark:text-gray-100"
                                  numberOfLines={1}
                                >
                                  {category.categoryName}
                                </Text>
                                {shouldShowReasoning && (
                                  <Text className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                    {category.reasoning}
                                  </Text>
                                )}
                              </View>
                              <View className={`rounded-full px-2 py-1 ${meta.pillClass}`}>
                                <Text className={`text-[11px] font-semibold ${meta.textClass}`}>
                                  {meta.label}
                                </Text>
                              </View>
                            </View>

                            <View className="mt-3 flex-row gap-2">
                              <View className="flex-1">
                                <Text className="text-[11px] text-gray-400 dark:text-gray-500">
                                  Current
                                </Text>
                                <Money className="text-sm text-gray-800 dark:text-gray-200">
                                  {formatMonthlyCurrency(category.currentMonthlyBudget)}
                                </Money>
                              </View>
                              <View className="flex-1">
                                <Text className="text-[11px] text-gray-400 dark:text-gray-500">
                                  90-day actual
                                </Text>
                                <Money className="text-sm text-gray-800 dark:text-gray-200">
                                  {formatMonthlyCurrency(category.actualMonthlyAverage)}
                                </Money>
                              </View>
                              <View className="flex-1">
                                <Text className="text-[11px] text-gray-400 dark:text-gray-500">
                                  Suggested
                                </Text>
                                <Money
                                  weight={500}
                                  className={
                                    category.suggestedMonthlyBudget > category.currentMonthlyBudget
                                      ? 'text-sm text-amber-700 dark:text-amber-400'
                                      : category.suggestedMonthlyBudget < category.currentMonthlyBudget
                                        ? 'text-sm text-green-700 dark:text-green-400'
                                        : 'text-sm text-gray-800 dark:text-gray-200'
                                  }
                                >
                                  {formatMonthlyCurrency(category.suggestedMonthlyBudget)}
                                </Money>
                              </View>
                            </View>

                            {hasPendingChange && (
                              <View className="mt-3 rounded-lg bg-gray-50 dark:bg-gray-900/40 px-3 py-2">
                                <View className="flex-row items-center justify-between gap-3">
                                  <Text className="text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                    Pending change
                                  </Text>
                                  <Money
                                    weight={500}
                                    className={`text-xs ${budgetDeltaTextClass(pendingMonthlyDelta)}`}
                                  >
                                    {formatSignedMonthlyCurrency(pendingMonthlyDelta)}
                                  </Money>
                                </View>
                                <Text className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                                  After changes: {formatMonthlyCurrency(proposedMonthlyBudget)}
                                </Text>
                              </View>
                            )}

                            {category.currentMonthlyBudget > 0 && (
                              <View className="mt-3 flex-row items-center gap-2">
                                <View className="flex-1">
                                  <ProgressBar
                                    value={budgetRatio}
                                    color={
                                      category.decision === 'review_outliers'
                                        ? 'bg-amber-500'
                                        : budgetRatio > 1
                                          ? 'bg-red-500'
                                          : 'bg-green-500'
                                    }
                                    height={4}
                                  />
                                </View>
                                <Text className="text-xs font-medium text-gray-500 dark:text-gray-400 min-w-[45px] text-right">
                                  {formatPercent(budgetRatio)}
                                </Text>
                              </View>
                            )}

                            {category.outliers.length > 0 && (
                              <View className="mt-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 px-3 py-2">
                                <Text className="text-xs font-medium text-amber-700 dark:text-amber-400">
                                  Potential one-offs: {formatCurrency(category.outlierTotal)}
                                </Text>
                                {category.outliers.slice(0, 3).map((outlier) => (
                                  <Text
                                    key={outlier.id}
                                    className="mt-1 text-[11px] text-amber-700/80 dark:text-amber-400/80"
                                    numberOfLines={1}
                                  >
                                    {formatDate(outlier.date)} - {outlier.merchantName ?? outlier.name}{' '}
                                    {formatCurrency(outlier.amount)}
                                  </Text>
                                ))}
                              </View>
                            )}

                            {category.lifeEventExcludedTotal > 0 && (
                              <Text className="mt-2 text-[11px] text-amber-700 dark:text-amber-400">
                                {formatCurrency(category.lifeEventExcludedTotal)} linked to life events was excluded.
                              </Text>
                            )}

                            {monthlySavings > 0 && category.decision === 'revise_down' && (
                              <Text className="mt-2 text-[11px] text-green-700 dark:text-green-400">
                                That is {formatCurrency(monthlySavings * 12)} less planned spending per year if it sticks.
                              </Text>
                            )}

                            {isEditing ? (
                              <View className="mt-3 gap-3">
                                <CurrencyInput
                                  label="Monthly budget"
                                  value={budgetDraft}
                                  onChangeText={(text) => {
                                    setBudgetDraft(text);
                                    setBudgetError(null);
                                  }}
                                  placeholder="0.00"
                                  size="sm"
                                  autoFocus
                                />
                                <View className="flex-row gap-2">
                                  <Button
                                    size="sm"
                                    className="flex-1"
                                    loading={savingCategoryId === displayId}
                                    disabled={savingCategoryId !== null && savingCategoryId !== displayId}
                                    onPress={() => {
                                      const parsed = parseFloat(budgetDraft);
                                      void stageCategoryBudget(category, parsed);
                                    }}
                                  >
                                    Stage change
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="secondary"
                                    className="flex-1"
                                    disabled={savingCategoryId === displayId}
                                    onPress={() => {
                                      setEditingCategoryId(null);
                                      setBudgetDraft('');
                                      setBudgetError(null);
                                    }}
                                  >
                                    Cancel
                                  </Button>
                                </View>
                              </View>
                            ) : (
                              <View className="mt-3 flex-row gap-2">
                                <Button
                                  size="sm"
                                  className="flex-1"
                                  disabled={
                                    isDemo ||
                                    !category.isEditable ||
                                    savingCategoryId !== null ||
                                    category.suggestedMonthlyBudget === proposedMonthlyBudget
                                  }
                                  loading={savingCategoryId === displayId}
                                  onPress={() =>
                                    void stageCategoryBudget(
                                      category,
                                      category.suggestedMonthlyBudget,
                                    )
                                  }
                                >
                                  {category.pendingMonthlyBudget === category.suggestedMonthlyBudget
                                    ? 'Suggested staged'
                                    : 'Stage suggested'}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  className="flex-1"
                                  disabled={isDemo || !category.isEditable || savingCategoryId !== null}
                                  onPress={() => startEditing(category)}
                                >
                                  {hasPendingChange ? 'Edit staged' : 'Edit'}
                                </Button>
                              </View>
                            )}
                          </View>
                        );
                      })}
                      </View>
                    </Card>
                  );
                })}
            </View>
          )}
        </PageContainer>
      </ScrollView>
    </>
  );
}
